import type { GeneratedContentPage } from '~/utils/content-pages';
import { useI18n } from 'vue-i18n';
import { useStore } from '#imports';
import { usePageActions } from '#layers/spaces/composables/use-page-actions';
import { blockRenderDataFactory } from '#layers/spaces/utils/space-factory';
import { createContentDetailBlockRenderData, findGeneratedContentPage, getContentPageOverride, getContentPageOverrideId } from '~/utils/content-pages';
import { useContentPages } from './use-content-pages';
import { useToastResultHandling } from './use-toast-result-handling';

export function useContentPageCustomization() {
	const { t } = useI18n();
	const store = useStore();
	const { editPage } = usePageActions();
	const { generatedContentPages } = useContentPages();

	const { onError } = useToastResultHandling({
		error: {
			title: t('page.customize_page_error'),
		},
	});

	return {
		customizeContentDetailPage,
	};

	async function customizeContentDetailPage(collectionId: NonNullable<GeneratedContentPage['collectionId']>) {
		try {
			const generatedPage = findGeneratedContentPage(generatedContentPages.value, {
				collectionId,
				type: 'detail',
				intent: 'edit',
			});

			if (!generatedPage) {
				return;
			}

			const overridePageId = getContentPageOverrideId(generatedPage);
			const overridePage = getContentPageOverride(store, generatedPage);

			const customPage = overridePage ?? await store.MonospacePage.create({
				id: crypto.randomUUID(),
				title: generatedPage.title,
				route: `${generatedPage.route}-custom`,
				icon: generatedPage.icon,
				isFullWidth: generatedPage.isFullWidth,
				spaceId: null,
				sectionId: generatedPage.sectionId,
				type: generatedPage.type,
				collectionId: generatedPage.collectionId,
				intent: generatedPage.intent,
				overridePage: overridePageId,
				sort: generatedPage.sort,
				isHidden: generatedPage.isHidden,
				description: generatedPage.description,
			});

			const customPageBlock = await store.MonospaceBlock.findFirst({
				filter: {
					pageId: { _eq: customPage.id },
				},
				fetchPolicy: 'fetch-only',
			});

			if (!customPageBlock) {
				await store.MonospaceBlock.create({
					id: crypto.randomUUID(),
					name: 'Page Block',
					category: 'page',
					pageId: customPage.id,
					renderData: blockRenderDataFactory(createContentDetailBlockRenderData()),
				});
			}

			await editPage(customPage.id, {
				saveCurrentPath: true,
			});
		}
		catch (error) {
			onError(error);
		}
	}
}
